import apiClient from './api';


/**
 * Submits an approval or rejection decision for an entity (Account or Order).
 * @param {object} payload - The data to send for approval.
 * @param {number} payload.entityId - The ID of the entity (Account or Order).
 * @param {string} payload.entityType - The type of the entity ('Account' or 'Order').
 * @param {number} payload.currentManagerAccountId - The Account ID of the logged-in manager/validator.
 * @param {boolean} payload.isApproved - True for approval, false for rejection.
 * @param {string} payload.comments - Required comments for the decision.
 * @param {number} payload.domainId - The ID of the domain.
 * @returns {Promise<boolean>} - True if the submission was successful, false otherwise.
 * @throws {Error} - Throws an error with a user-friendly message if the API call fails.
 */
export const submitApproval = async (payload) => {
  try {
    const response = await apiClient.post('/api/Approvals/submit', payload);

    // Your API returns 200 OK and "Décision soumise avec succès." on success
    if (response.status === 200) {
      return true; // Indicate success
    } else {
      // This case might not be hit if your API always throws for non-200, but good for safety
      throw new Error('La soumission de la décision a échoué pour une raison inconnue.');
    }
  } catch (error) {
    let errorMessage = 'Une erreur est survenue lors de la soumission de la décision.';

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const statusCode = error.response.status;
      const backendMessage = error.response.data; // This can be a string or an object

      console.error(`API Error - Status: ${statusCode}, Response:`, backendMessage);

      if (typeof backendMessage === 'string' && backendMessage.length > 0) {
        // If the backend directly returns a string error message (e.g., "La soumission de la décision a échoué.")
        errorMessage = backendMessage;
      } else if (typeof backendMessage === 'object' && backendMessage.message) {
        // If the backend returns an object with a 'message' property
        errorMessage = backendMessage.message;
      } else {
        // Fallback for specific HTTP status codes without a clear message
        switch (statusCode) {
          case 400:
            errorMessage = "Requête invalide. Veuillez vérifier les informations fournies.";
            break;
          case 403:
            errorMessage = "Accès non autorisé. Vous n'avez pas la permission d'effectuer cette action.";
            break;
          case 500:
            errorMessage = "Erreur interne du serveur. Veuillez réessayer plus tard.";
            break;
          default:
            errorMessage = `Une erreur inattendue est survenue (Code: ${statusCode}).`;
            break;
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = "Impossible de se connecter au serveur. Vérifiez votre connexion internet ou l'état du service.";
    } else {
      // Something happened in setting up the request that triggered an Error
      errorMessage = error.message; // Generic JS error
    }
    // Re-throw the refined error message so the calling component can catch it and display a toast
    throw new Error(errorMessage);
  }
};